import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    // Ensure video never receives touch events (prevents iOS Safari native controls on tap)
    videoElement.style.pointerEvents = 'none';

    // Hide video before sources are injected to prevent iOS Safari control flash on load
    videoElement.style.opacity = '0';
    videoElement.style.transition = 'opacity 0.3s ease';

    // Ensure muted is set programmatically as well as via attribute
    videoElement.muted = true;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;
      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();

    // Reveal once playing — this is after Safari has finished its load/play init
    const reveal = () => {
      videoElement.style.opacity = '1';
    };

    videoElement.addEventListener('canplay', reveal, { once: true });

    // Fallback in case canplay doesn't fire (slow connection, prefers-reduced-motion, etc.)
    setTimeout(reveal, 1500);
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}